import React, { useState } from "react";
import UserApi from "../../api/UserApi";
import IconHidePass from "../../Icons/HidePassword";
import IconInfo from "../../Icons/NotificationMessage/Info";
import IconShowPass from "../../Icons/ShowPassword";
import LoadingSpinner from "../../Loading";
import { EUserRole } from "../../Model/User";
import UserUtil from "../../Utility/UserUtil";
import MtiButton from "../Buttons/MtiButton";
import "./CreateNewUser.css";

export type Props = {
  userNames: string[];
  onComplete: () => void;
};

const PASSWORD_MISSMATCH = "Passwords don't match. Try again.";
const PASSWORD_EMPTY = "Enter a password.";
const PASSWORD_CONFIRM_EMPTY = "Confirm your password.";
const USERNAME_EMPTY = "Choose a username.";
const USERNAME_TAKEN = "This username is already taken. Please choose another username.";
const PASSWORD_WEAK =
  "Password must be 6 to 20 characters which contain at least one numeric digit, one uppercase and one lowercase letter";

export default function CreateNewUser({ userNames, onComplete }: Props) {
  const [userName, setUserName] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showLoading, setShowLoading] = useState(false);
  const [userNameError, setUserNameError] = useState(null as string | null);
  const [passwordError, setPasswordError] = useState(null as string | null);

  const onSubmit = () => {
    let isFailed = false;

    if (!UserUtil.IsUserPasswordStrong(password)) {
      setPasswordError(PASSWORD_WEAK);
      isFailed = true;
    }

    if (password !== passwordConfirm) {
      setPasswordError(PASSWORD_MISSMATCH);
      isFailed = true;
    }
    if (passwordConfirm === "") {
      setPasswordError(PASSWORD_CONFIRM_EMPTY);
      isFailed = true;
    }
    if (password === "") {
      setPasswordError(PASSWORD_EMPTY);
      isFailed = true;
    }

    if (userNames.includes(userName)) {
      setUserNameError(USERNAME_TAKEN);
      isFailed = true;
    }

    if (userName === "") {
      setUserNameError(USERNAME_EMPTY);
      isFailed = true;
    }

    if (isFailed) {
      return;
    }

    setShowLoading(true);
    UserApi.createUser({
      isActive: true,
      userName: userName,
      password: password,
      userEmail: "",
      name: "",
      surname: "",
      dateCreated: "",
      userRole: EUserRole.Regular,
    }).finally(() => {
      setShowLoading(false);
      onComplete();
    });
  };

  const toggleShowPass = () => {
    setShowPassword(!showPassword);
  };

  const resetFailStatus = () => {
    setPasswordError(null);
    setUserNameError(null);
  };

  return (
    <div className="create-new-user-form">
      <span className="create-new-user-title">Create New Account</span>
      <input
        className="create-new-user-username"
        name="username"
        value={userName}
        placeholder="Username"
        onChange={(e) => setUserName(e.target.value)}
        onClick={resetFailStatus}
        autoComplete="new-password"
      />

      <div className="create-new-user-fail">
        {userNameError != null && (
          <>
            <IconInfo fill="#C92932" width="10" height="10" />
            <p>{userNameError}</p>{" "}
          </>
        )}
      </div>

      <div
        className="create-new-user-password"
        tabIndex={1}
        style={{
          marginTop: 20,
        }}
      >
        <input
          className="create-new-user-password-input"
          name="password"
          type={showPassword ? "text" : "password"}
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onClick={resetFailStatus}
          autoComplete="new-password"
          style={{}}
        />
        <button onClick={toggleShowPass} className="create-new-user-password-button">
          {showPassword && <IconShowPass width="17" height="17" />}
          {!showPassword && <IconHidePass width="17" height="17" />}
        </button>
      </div>

      <div
        className="create-new-user-password"
        tabIndex={2}
        style={{
          marginTop: 16,
        }}
      >
        <input
          className="create-new-user-password-input"
          name="password-confirm"
          type={showPassword ? "text" : "password"}
          placeholder="Confirm Password"
          value={passwordConfirm}
          onChange={(e) => setPasswordConfirm(e.target.value)}
          onClick={resetFailStatus}
        />
        <button onClick={toggleShowPass} className="create-new-user-password-button">
          {showPassword && <IconShowPass width="17" height="17" />}
          {!showPassword && <IconHidePass width="17" height="17" />}
        </button>
      </div>

      <div className="create-new-user-fail">
        {passwordError != null && (
          <>
            <IconInfo fill="#C92932" width="10" height="10" />
            <p>{passwordError}</p>
          </>
        )}
      </div>

      <div className="create-new-user-buttons">
        <MtiButton size="large" onClick={onSubmit} style={{ width: "100%", margin: 0 }}>
          Create Account
        </MtiButton>
      </div>

      {showLoading && <LoadingSpinner />}
      <button className="create-new-user-close-button" onClick={() => onComplete()}>
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M6 7L11 12L12 11L7 6L12 1L11 0L6 5L1 0L0 1L5 6L0 11L1 12L6 7Z" fill="#F89723" />
        </svg>
      </button>
    </div>
  );
}
